import { MAPBOX_TOKEN } from './mapbox';

export interface GeocodingResult {
  latitude: number;
  longitude: number;
  formattedAddress: string;
}

export interface GeocodingError {
  error: string;
}

/**
 * Geocode an address using Mapbox Geocoding API
 */
export const geocodeAddress = async (
  address: string
): Promise<GeocodingResult | GeocodingError> => {
  if (!MAPBOX_TOKEN) {
    console.error('Mapbox token is not configured');
    return { error: 'Mapbox token is not configured' };
  }

  if (!address || address.trim() === '') {
    return { error: 'Address is required' };
  }

  try {
    const encodedAddress = encodeURIComponent(address);
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedAddress}.json?access_token=${MAPBOX_TOKEN}&limit=1`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Geocoding failed: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.features || data.features.length === 0) {
      return { error: 'Address not found' };
    }

    const feature = data.features[0];
    const [longitude, latitude] = feature.center;

    return {
      latitude,
      longitude,
      formattedAddress: feature.place_name,
    };
  } catch (error) {
    console.error('Geocoding error:', error);
    return {
      error: error instanceof Error ? error.message : 'Failed to geocode address',
    };
  }
};

/**
 * Geocode multiple addresses in parallel
 */
export const geocodeAddresses = async (
  addresses: string[]
): Promise<(GeocodingResult | GeocodingError)[]> => {
  return Promise.all(addresses.map((address) => geocodeAddress(address)));
};

/**
 * Calculate distance between two coordinates (in km) using Haversine formula
 */
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};
